import { datadogLogs } from "@datadog/browser-logs";
import { appState, showError, useAppDispatch, useAppSelector } from "app";
import clsx from "clsx";
import { IconColors, SvgIcon, QQLink } from "common/components";
import {
  downloadFileWithSessionSafely,
  ErrorMap,
  ErrorType,
  useEgerdauToken,
} from "common/services";
import React from "react";
import { useDropzone } from "react-dropzone";
import styles from "./FileDropArea.module.scss";
import { trackUploadFile, trackDownloadTemplate } from "common/tagManagerService";
import { TemplateDownloadUrl } from "../utils";

const invalidExtError = ErrorMap.get(ErrorType.UploadFormatInvalid);
const uploadMultipleError = ErrorMap.get(ErrorType.UploadMultiple);

interface FileDropAreaProps {
  disabled?: boolean;
  onFileSelected?: (file: File, area: "excel" | "intelligent") => void;
  selectedFile?: File | null;
  selectedArea?: "excel" | "intelligent" | null;
  onRemoveFile?: () => void;
}

const downloadTemplateWithToken = async (token: string) =>
  await downloadFileWithSessionSafely(TemplateDownloadUrl, token, "Gerdau template download");

export const FileDropArea: React.FC<FileDropAreaProps> = ({
  disabled = false,
  onFileSelected = () => {},
  selectedFile = null,
  selectedArea = null,
  onRemoveFile = () => {},
}) => {
  const dispatch = useAppDispatch();
  const { customer } = useAppSelector(appState);
  const token = useEgerdauToken();
  const gerdauEmailDomain = "@gerdau.com";

  // GenAI upload is now enabled for all users; we keep these for logging only.
  const isGerdauUser = customer?.uid?.includes(gerdauEmailDomain) || false;
  const genAiEnabled = true;

  const handleDownloadClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    trackDownloadTemplate();
    downloadTemplateWithToken(token);
  };

  const reportMultiple = (acceptedFiles: File[]) => {
    datadogLogs.logger.error("[File Drop Area] File error", {
      name: "File error: Invalid file",
      details: acceptedFiles,
    });
    dispatch(
      showError({
        critical: false,
        title: uploadMultipleError?.title,
        message: uploadMultipleError?.message,
      }),
    );
  };

  const handleOk = (file: File, area: "excel" | "intelligent", okMsg: string) => {
    datadogLogs.logger.info("[File Drop Area] File Type Correct", {
      name: okMsg,
      details: file.name,
      isGerdauUser,
      genAiEnabled,
    });
    onFileSelected(file, area);
  };

  const handleInvalid = (fileName: string, where: string) => {
    datadogLogs.logger.error("[File Drop Area] File Type error", {
      name: `File Type error: Not supported formats (${where})`,
      details: fileName,
      isGerdauUser,
      genAiEnabled,
    });
    dispatch(
      showError({
        critical: false,
        title: invalidExtError?.title,
        message: invalidExtError?.message,
      }),
    );
  };

  // Single merged dropzone for all users: accepts Excel template and PDF
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles: File[]) => {
      trackUploadFile();
      if (acceptedFiles.length !== 1) return reportMultiple(acceptedFiles);

      const file = acceptedFiles[0];
      const name = file.name.toLowerCase();

      const isExcel = name.endsWith(".xlsx");
      const isPdf = name.endsWith(".pdf");

      if (!isExcel && !isPdf) {
        handleInvalid(file.name, "merged area");
        return;
      }

      const area: "excel" | "intelligent" = isPdf ? "intelligent" : "excel";
      const fileTypeLabel = isPdf ? "PDF" : "XLS/XLSX";

      handleOk(file, area, `File Type correct: ${fileTypeLabel}`);
    },
    disabled,
    noClick: disabled,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/pdf": [".pdf"],
    },
    multiple: false,
  });

  const titleClass = "text-dark font-size-16 fw-bold m-0";
  const formatsClass = "text-dark font-size-12 fw-bold m-0 mt-2";

  const IconWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div
      className="mx-auto d-flex align-items-center justify-content-center mb-3"
      style={{
        width: 62,
        height: 44,
        lineHeight: 0,
      }}
    >
      {children}
    </div>
  );
  const hasFileSelected = !!selectedFile || !!selectedArea;

  return (
    <div className="mb-4">
      <div className={styles.sectionContainer}>
        {/* Intelligent upload description */}
        <div className="mb-3">
          <div className="d-flex align-items-center">
            <h5 className="fw-bold mb-0 me-2">Intelligent Upload*</h5>
            <span className={clsx(styles.betaBadge, "badge bg-warning")}>BETA</span>
          </div>
          <p className="text-muted font-size-14 mb-0">
            Upload the quotation request as a PDF/ XLSX (can be non-template-based) and let us
            import the data for you.
          </p>
          <p className="text-muted font-size-14 mb-0">
            <b>Average Limit of 500 items.</b>
          </p>
        </div>
      </div>

      <div
        className={clsx(
          "rounded cursor-pointer mx-auto text-center",
          styles.dropArea,
          hasFileSelected ? styles.selected : styles.unselected,
          disabled && styles.disabled,
        )}
        {...getRootProps()}
      >
        <div className="py-5">
          <IconWrapper>
            <SvgIcon
              name="cloud-ai-effect"
              attributes={{
                width: 62,
                height: 44,
                preserveAspectRatio: "xMidYMid meet",
                fill: hasFileSelected ? IconColors.SUCCESS : IconColors.PRIMARY,
                style: { display: "block" },
              }}
            />
          </IconWrapper>
          <p className={titleClass}>Drag your file here or click to upload</p>
          <p className={formatsClass}>Accepted File Format(s): .xlsx, .pdf</p>
          {hasFileSelected && selectedFile && (
            <div className={clsx(styles.selectedFileInfo, "mt-3")}>
              <SvgIcon
                name="xlsx-document"
                attributes={{ width: "16px", height: "16px", fill: "#000000" }}
                className="me-2"
              />
              <span className={styles.fileName} title={selectedFile.name}>
                {selectedFile.name}
              </span>
            </div>
          )}
          <input {...getInputProps()} />
        </div>
      </div>

      <div className="text-center mt-4">
        {hasFileSelected ? (
          <button
            onClick={onRemoveFile}
            className={clsx(styles.removeFileBtn, "btn btn-danger btn-sm")}
          >
            Remove File to Upload
          </button>
        ) : (
          <span className="text-muted font-size-14">No File Selected</span>
        )}
      </div>

      {/* Template download section moved here */}
      <div className={clsx(styles.templateDownloadArea, "mb-4", "mt-4")}>
        <span className="font-size-14">
          No file? No problem. Use our{" "}
          <QQLink onClick={handleDownloadClick} href="#" className="text-primary font-size-14">
            Excel template
          </QQLink>{" "}
          to get started.
        </span>
      </div>
    </div>
  );
};
